import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getAllProducts } from "../../Services/ProductService";

// Load localStorage safely
const loadSelectedProductsFromStorage = () => {
  try {
    const saved = localStorage.getItem("customise-selectedProducts");
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    console.error("Error parsing saved products", e);
    return [];
  }
};

const ChooseAddOn = ({
  onSelectionChange,
  selectedProducts: initialSelectedProducts = [],
  onProductsLoaded,
}) => {
  const location = useLocation();

  const [allProducts, setAllProducts] = useState({
    wheat: [],
    grains: [],
    pulses: [],
  });

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(false);

  const wheatProducts = allProducts.wheat;
  const grainsProducts = allProducts.grains;
  const pulsesProducts = allProducts.pulses;

  // -------------------------------------------------------
  // 1. LOAD PRODUCTS (RUN ONLY ONCE)
  // -------------------------------------------------------
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const fetchProducts = async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams(location.search);
        const isEdit = params.get("type") === "edit";

        if (isEdit) {
          const saved = loadSelectedProductsFromStorage();
          const ids = saved.map((p) => p.id);
          const response = await getAllProducts({ productIds: ids });
          const all = response.products || [];
          setAllProducts({
            wheat: all.filter((p) => p.categoryId === 1),
            grains: all.filter((p) => p.categoryId === 2),
            pulses: all.filter((p) => p.categoryId === 3),
          });
        } else {
          const response = await getAllProducts({
            categoryIds: [1, 2, 3],
            isCustomProduct: false,
          });
          const all = response.products || [];
          setAllProducts({
            wheat: all.filter((p) => p.categoryId === 1),
            grains: all.filter((p) => p.categoryId === 2),
            pulses: all.filter((p) => p.categoryId === 3),
          });
        }
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
        onProductsLoaded && onProductsLoaded();
      }
    };

    fetchProducts();
  }, []);

  // -------------------------------------------------------
  // 2. SET SELECTED PRODUCTS — AFTER PRODUCTS LOADED
  // -------------------------------------------------------
  useEffect(() => {
    if (loading) return;

    let finalSelected = [];

    // Priority 1 → localStorage
    const fromStorage = loadSelectedProductsFromStorage();
    if (fromStorage.length > 0) {
      finalSelected = fromStorage.map((p) => Number(p.id));
    }

    // Priority 2 → props
    if (finalSelected.length === 0 && initialSelectedProducts.length > 0) {
      finalSelected = initialSelectedProducts.map((p) => Number(p.id));
    }

    // Priority 3 → URL ?productId=
    if (finalSelected.length === 0) {
      const params = new URLSearchParams(location.search);
      const pid = params.get("productId");
      if (pid) finalSelected = [Number(pid)];
    }

    setSelectedProducts(finalSelected);
  }, [loading]);

  // -------------------------------------------------------
  // 3. NOTIFY PARENT WHEN SELECTION CHANGES
  // -------------------------------------------------------
  useEffect(() => {
    if (!onSelectionChange) return;
    const all = [...wheatProducts, ...grainsProducts, ...pulsesProducts];
    const selectedData = all.filter((p) => selectedProducts.includes(p.id));
    onSelectionChange(selectedData);
  }, [selectedProducts, wheatProducts, grainsProducts, pulsesProducts]);

  // -------------------------------------------------------
  // SELECTION HANDLERS
  // -------------------------------------------------------
  const handleCheckboxChange = (productId, checked) => {
    setSelectedProducts((prev) =>
      checked ? [...prev, productId] : prev.filter((id) => id !== productId)
    );
  };

  const handleRadioChange = (productId) => {
    const wheatIds = wheatProducts.map((p) => p.id);
    setSelectedProducts((prev) => [
      ...prev.filter((id) => !wheatIds.includes(id)),
      productId,
    ]);
  };

  // -------------------------------------------------------
  // RENDER LIST
  // -------------------------------------------------------
  const renderProductList = (products, categoryName, categoryId) => {
    if (!products.length) {
      return (
        <p className="text-center text-muted">No {categoryName} found</p>
      );
    }

    const isWheat = categoryId === 1;

    return (
      <div
        className="checkbox-lis"
        style={{ maxHeight: "300px", overflowY: "auto", paddingRight: "10px" }}
      >
        {products.map((product) => {
          const price =
            categoryId === 1
              ? product.discountPrice || 0
              : product.price || 0;

          return (
            <div
              key={product.id}
              className="d-flex align-items-center justify-content-between gap-2 check-box-div"
            >
              <div className={`form-check ${isWheat ? "custom-radio" : "custom-checkbox"}`}>
                <input
                  className="form-check-input"
                  type={isWheat ? "radio" : "checkbox"}
                  name={isWheat ? "wheat" : undefined}
                  value={product.id}
                  id={`product-${product.id}`}
                  checked={selectedProducts.includes(product.id)}
                  onChange={(e) =>
                    isWheat
                      ? handleRadioChange(product.id)
                      : handleCheckboxChange(product.id, e.target.checked)
                  }
                />
                <label className="form-check-label" htmlFor={`product-${product.id}`}>
                  {product.name}
                </label>
              </div>
              <p className="fs_12 fw_500 mb-0 text-colors">
                {price} ₹ {" / KG"}
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div className="section-title chose-quantity">
        <h5>Choose Your Best Add-on</h5>
        <p>Select from our premium grains to start your custom flour mix.</p>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <p>Loading products...</p>
        </div>
      ) : (
        <div className="content-box">
          <div className="choose-your-grid">
            {/* WHEAT — Mandatory */}
            <div className="grid-box">
              <div className="d-flex align-items-center justify-content-between gap-2 mb_20">
                <h5 className="fs_24 fw_700 lh-1 mb-0 text-capitalize">
                  Wheat ({wheatProducts.length})
                </h5>
                <span
                  className="tag fs_12 fw_600"
                  style={{ backgroundColor: '#dc3545', color: '#fff', padding: '2px 8px', borderRadius: '4px' }}
                >
                  Required
                </span>
              </div>
              <p className="fs_12 text-muted mb_10" style={{ marginTop: '-10px' }}>
                At least one wheat must be selected.
              </p>
              {renderProductList(wheatProducts, "wheat products", 1)}
            </div>

            {/* GRAINS — Optional */}
            <div className="grid-box">
              <div className="d-flex align-items-center justify-content-between gap-2 mb_20">
                <h5 className="fs_24 fw_700 lh-1 mb-0 text-capitalize">
                  Grains ({grainsProducts.length})
                </h5>
                <span
                  className="tag fs_12 fw_500"
                  style={{ backgroundColor: '#6c757d', color: '#fff', padding: '2px 8px', borderRadius: '4px' }}
                >
                  Optional
                </span>
              </div>
              {renderProductList(grainsProducts, "grain products", 2)}
            </div>

            {/* PULSES — Optional */}
            <div className="grid-box">
              <div className="d-flex align-items-center justify-content-between gap-2 mb_20">
                <h5 className="fs_24 fw_700 lh-1 mb-0 text-capitalize">
                  Pulses ({pulsesProducts.length})
                </h5>
                <span
                  className="tag fs_12 fw_500"
                  style={{ backgroundColor: '#6c757d', color: '#fff', padding: '2px 8px', borderRadius: '4px' }}
                >
                  Optional
                </span>
              </div>
              {renderProductList(pulsesProducts, "pulses", 3)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChooseAddOn;
